import { MontoRequest } from "@/shared/types/monto";
import { NumberFormatValues } from "react-number-format";
import { getTotalMonto } from "@/features/monetizacion/shared/utils";
import { Badge, Box, IconButton, Stack, Typography } from "@mui/material";
import PaymentsIcon from '@mui/icons-material/Payments';
import MontoFieldModal from "./monto-field-modal";
import ClearIcon from '@mui/icons-material/Clear';
import { useId, useRef } from "react";
import { useOpenModal } from "@/components/modal";
import FocusableBox from "../../_shared/focusable-box";
import { formatNumber } from "@/shared/formats";
import { mergeRefs } from "@/shared/utils";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import NumberField, { NumberFieldProps } from "../number-field";

export interface MontoFieldProps extends Omit<NumberFieldProps, "value" | "onValueChange"> {
    monedaId?: string,
    objective?: number,
    objectiveFilled?: number,
    emotionalDamage?: boolean,
    value?: MontoRequest | null,
    onValueChange?: (monto: MontoRequest) => any
}

export default function MontoField({
    monedaId: receivedMonedaId,
    objective,
    objectiveFilled,
    emotionalDamage,
    value = null,
    onValueChange = () => { },
    allowNegative,
    decimalScale,
    inputRef,
    ...props
}: MontoFieldProps) {
    const ref = useRef<HTMLElement>(null!);

    const openModal = useOpenModal();
    const { monedas, monedaBase, cotizacion } = useMonetizacion();

    const monedaId = receivedMonedaId ? receivedMonedaId : monedaBase.id;
    const moneda = monedas.find(m => m.id === monedaId)!;

    const getInputValue = () => {
        if (!value || value.items.length <= 0) return "";
        return getTotalMonto(value, cotizacion, moneda.id);
    };

    const handleChange = (values: NumberFormatValues) => {
        const { floatValue } = values;
        if (floatValue === undefined) {
            onValueChange({ items: [] });
            return;
        }

        onValueChange({ items: [{ monedaId: monedaId, cantidad: floatValue }] })
    }

    const modalCode = "monto-field-modal-" + useId();

    const nonMonedaCount = (value && value.items.filter(vi => vi.monedaId !== moneda.id).length) || 0;
    const shouldShowNormalInput = !value || nonMonedaCount <= 0;

    const slotProps = {
        ...props.slotProps,
        input: {
            ...props.slotProps?.input,
            endAdornment: <Stack direction={"row"} gap={1} alignItems={"center"}>
                <Box> &nbsp;{moneda.simbolo} </Box>
                {!shouldShowNormalInput && <Box>
                    <IconButton disabled={props.disabled} edge="end" onClick={e => {
                        e.stopPropagation();
                        onValueChange({ items: [] });
                        ref.current.focus();
                    }}>
                        <ClearIcon />
                    </IconButton>
                </Box>}
                <Box>
                    <IconButton disabled={props.disabled} edge="end" onClick={e => {
                        e.stopPropagation();
                        openModal(modalCode)
                    }}>
                        {!shouldShowNormalInput ? <Badge badgeContent={nonMonedaCount} color="primary">
                            <PaymentsIcon />
                        </Badge> : <PaymentsIcon />}
                    </IconButton>
                </Box>
                <Box> {(props.slotProps?.input as any)?.endAdornment} </Box>
            </Stack>
        }
    }

    return <>
        {shouldShowNormalInput ?
            <NumberField
                {...props}
                inputRef={mergeRefs(ref, inputRef)}
                value={getInputValue()}
                onValueChange={handleChange}
                slotProps={slotProps}
                allowNegative={allowNegative}
                decimalScale={decimalScale}
                onKeyDown={e => {
                    props.onKeyDown && props.onKeyDown(e);
                    if (!e.defaultPrevented && e.key == " ") {
                        e.preventDefault();
                        openModal(modalCode)
                    }
                }}
            /> :
            <FocusableBox
                {...props}
                inputRef={mergeRefs(ref, inputRef)}
                slotProps={slotProps}
                onClick={e => {
                    props.onClick && props.onClick(e);
                    if (!e.defaultPrevented) openModal(modalCode)
                }}
                onKeyDown={e => {
                    props.onKeyDown && props.onKeyDown(e);
                    if (!e.defaultPrevented && e.key == " ") {
                        e.preventDefault();
                        openModal(modalCode)
                    }
                }}
            >
                <Stack minWidth={0}>
                    <Typography noWrap textAlign={"right"}><i>(Aprox.)</i> {formatNumber(getInputValue() || 0)}</Typography>
                </Stack>
            </FocusableBox>}
        <MontoFieldModal code={modalCode} moneda={moneda} value={value} onValueChange={onValueChange}
            allowNegative={allowNegative} decimalScale={decimalScale}
            objective={objective} objectiveFilled={objectiveFilled} emotionalDamage={emotionalDamage}
            onClose={() => ref.current.focus()} />
    </>
}